import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PDFDocument, rgb, degrees, StandardFonts } from "pdf-lib";
import api, { API, getToken, formatApiError } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { useCoffee } from "@/context/CoffeeContext";
import { CoffeeModal } from "@/components/CoffeeModal";
import { PAGE, BTN_PRIMARY, BTN_GHOST, ICON_BTN } from "@/lib/styles";
import {
  ArrowLeft, Pen, Highlighter, Type, PenTool, Layers, MousePointer2, Save,
  ChevronLeft, ChevronRight, Trash2, RotateCw, RotateCcw, ArrowUp, ArrowDown, X,
  Coffee, Loader2, Undo2,
} from "lucide-react";
import { toast } from "sonner";

let _pdfjs = null;
async function getPdfjs() {
  if (!_pdfjs) {
    _pdfjs = await import("pdfjs-dist");
    _pdfjs.GlobalWorkerOptions.workerSrc = "https://unpkg.com/pdfjs-dist@4.8.69/build/pdf.worker.min.mjs";
  }
  return _pdfjs;
}

function hexToRgb(hex) {
  const h = hex.replace("#", "");
  return rgb(parseInt(h.slice(0, 2), 16) / 255, parseInt(h.slice(2, 4), 16) / 255, parseInt(h.slice(4, 6), 16) / 255);
}
function abToBase64(buf) {
  const bytes = new Uint8Array(buf);
  let bin = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  return btoa(bin);
}

const COLORS = ["#ef4444", "#3b82f6", "#10b981", "#111827", "#f59e0b"];

const TOOLS = [
  { id: "select", label: "Mover", Icon: MousePointer2 },
  { id: "pen", label: "Dibujar", Icon: Pen },
  { id: "highlight", label: "Resaltar", Icon: Highlighter },
  { id: "text", label: "Texto", Icon: Type },
  { id: "sign", label: "Firmar", Icon: PenTool },
  { id: "pages", label: "Páginas", Icon: Layers },
];

// ---------- Signature pad ----------
function SignaturePad({ onClose, onDone }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const has = useRef(false);
  const pos = (e) => {
    const r = ref.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  };
  const start = (e) => { drawing.current = true; const c = ref.current.getContext("2d"); const p = pos(e); c.beginPath(); c.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const c = ref.current.getContext("2d"); const p = pos(e); c.lineWidth = 2.5; c.lineCap = "round"; c.strokeStyle = "#0f172a"; c.lineTo(p.x, p.y); c.stroke(); has.current = true; };
  const end = () => { drawing.current = false; };
  const clear = () => { const c = ref.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); has.current = false; };
  return (
    <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="signature-modal">
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-heading font-medium text-lg mb-3">Dibuja tu firma</h3>
        <canvas ref={ref} width={420} height={180} className="bg-slate-50 dark:bg-slate-800 border border-dashed border-slate-300 dark:border-slate-700 rounded-lg touch-none"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end} data-testid="signature-canvas" />
        <div className="flex justify-between mt-4">
          <button onClick={clear} className={BTN_GHOST} data-testid="signature-clear"><Undo2 size={15} /> Limpiar</button>
          <div className="flex gap-2">
            <button onClick={onClose} className={BTN_GHOST} data-testid="signature-cancel">Cancelar</button>
            <button onClick={() => { if (!has.current) { toast.info("Dibuja tu firma primero"); return; } onDone(ref.current.toDataURL("image/png")); }} className={BTN_PRIMARY} data-testid="signature-done">Usar firma</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PdfEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { status, refresh } = useCoffee();
  const [coffeeOpen, setCoffeeOpen] = useState(false);

  const [bytes, setBytes] = useState(null);
  const [pdf, setPdf] = useState(null);
  const [meta, setMeta] = useState({ filename: "documento.pdf", folder_id: null });
  const [numPages, setNumPages] = useState(0);
  const [pageIdx, setPageIdx] = useState(0);
  const [scale, setScale] = useState(1);
  const [ptSize, setPtSize] = useState({ w: 1, h: 1 });
  const [loading, setLoading] = useState(true);

  const [intro, setIntro] = useState(true);
  const [tool, setTool] = useState("select");
  const [color, setColor] = useState("#ef4444");
  const [ann, setAnn] = useState({}); // { [pageIndex]: [ann] }
  const [order, setOrder] = useState([]); // [{index, rotation}]
  const [sigOpen, setSigOpen] = useState(false);
  const pendingSig = useRef(null);
  const [saving, setSaving] = useState(false);
  const [showSaveDlg, setShowSaveDlg] = useState(false);
  const [overwrite, setOverwrite] = useState(false);
  const [saveTitle, setSaveTitle] = useState("");

  const canvasRef = useRef(null);
  const drawing = useRef(null); // current stroke

  const active = status?.active;

  // Load pdf bytes + metadata
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const list = await api.get("/docs/files");
        const rec = list.data.find((d) => d.id === id);
        if (rec) setMeta({ filename: rec.filename, folder_id: rec.folder_id || null });
        setSaveTitle((rec?.filename || "documento").replace(/\.pdf$/i, "") + " (editado)");
        const url = `${API}/docs/files/${id}/download?auth=${getToken()}`;
        const buf = await (await fetch(url)).arrayBuffer();
        if (ignore) return;
        setBytes(buf);
        const pdfjs = await getPdfjs();
        const doc = await pdfjs.getDocument({ data: buf.slice(0) }).promise;
        if (ignore) return;
        setPdf(doc);
        setNumPages(doc.numPages);
        setOrder(Array.from({ length: doc.numPages }, (_, i) => ({ index: i, rotation: 0 })));
      } catch (err) {
        toast.error("No se pudo abrir el PDF");
        navigate("/documentos");
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [id, navigate]);

  // Render current page
  const renderPage = useCallback(async () => {
    if (!pdf || !canvasRef.current) return;
    const page = await pdf.getPage(pageIdx + 1);
    const unscaled = page.getViewport({ scale: 1 });
    const maxW = Math.min(820, window.innerWidth - 360);
    const s = maxW / unscaled.width;
    setScale(s);
    setPtSize({ w: unscaled.width, h: unscaled.height });
    const viewport = page.getViewport({ scale: s });
    const canvas = canvasRef.current;
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext("2d");
    await page.render({ canvasContext: ctx, viewport }).promise;
  }, [pdf, pageIdx]);

  useEffect(() => { renderPage(); }, [renderPage]);

  const pageAnns = ann[pageIdx] || [];
  const setPageAnns = (updater) => setAnn((prev) => ({ ...prev, [pageIdx]: typeof updater === "function" ? updater(prev[pageIdx] || []) : updater }));

  const norm = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height };
  };

  const onDown = (e) => {
    if (tool === "pen" || tool === "highlight") {
      const p = norm(e);
      drawing.current = { type: tool, color: tool === "highlight" ? "#fde047" : color, width: tool === "highlight" ? 14 : 2.5, points: [p] };
    } else if (tool === "text") {
      const p = norm(e);
      const text = window.prompt("Escribe el texto:");
      if (text && text.trim()) setPageAnns((a) => [...a, { type: "text", x: p.x, y: p.y, text: text.trim(), size: 16, color }]);
    } else if (tool === "sign" && pendingSig.current) {
      const p = norm(e);
      setPageAnns((a) => [...a, { type: "image", x: p.x, y: p.y, w: 0.28, dataUrl: pendingSig.current }]);
    }
  };
  const onMove = (e) => {
    if (!drawing.current) return;
    drawing.current.points.push(norm(e));
    setPageAnns((a) => a); // trigger re-render via state churn
    forceTick();
  };
  const [, setTick] = useState(0);
  const forceTick = () => setTick((t) => t + 1);
  const onUp = () => {
    if (drawing.current && drawing.current.points.length > 1) {
      const stroke = drawing.current;
      setPageAnns((a) => [...a, stroke]);
    }
    drawing.current = null;
  };

  const undo = () => setPageAnns((a) => a.slice(0, -1));

  // Pages ops
  const rotate = (i, dir) => setOrder((o) => o.map((e, k) => (k === i ? { ...e, rotation: (e.rotation + dir * 90 + 360) % 360 } : e)));
  const delPage = (i) => setOrder((o) => (o.length <= 1 ? o : o.filter((_, k) => k !== i)));
  const movePage = (i, dir) => setOrder((o) => {
    const j = i + dir;
    if (j < 0 || j >= o.length) return o;
    const c = [...o]; [c[i], c[j]] = [c[j], c[i]]; return c;
  });

  const chooseTool = (t) => {
    setIntro(false);
    if (t === "sign") { setSigOpen(true); setTool("sign"); }
    else setTool(t);
  };

  const doSave = async () => {
    setSaving(true);
    try {
      const src = await PDFDocument.load(bytes);
      const font = await src.embedFont(StandardFonts.Helvetica);
      for (const [pi, anns] of Object.entries(ann)) {
        const page = src.getPage(Number(pi));
        const { width, height } = page.getSize();
        for (const a of anns) {
          if (a.type === "text") {
            page.drawText(a.text, { x: a.x * width, y: height - a.y * height - a.size, size: a.size, font, color: hexToRgb(a.color) });
          } else if (a.type === "pen" || a.type === "highlight") {
            const col = hexToRgb(a.color);
            for (let i = 0; i < a.points.length - 1; i++) {
              const p0 = a.points[i], p1 = a.points[i + 1];
              page.drawLine({ start: { x: p0.x * width, y: height - p0.y * height }, end: { x: p1.x * width, y: height - p1.y * height }, thickness: a.width, color: col, opacity: a.type === "highlight" ? 0.35 : 1 });
            }
          } else if (a.type === "image") {
            const png = await src.embedPng(a.dataUrl);
            const w = a.w * width;
            const h = w * (png.height / png.width);
            page.drawImage(png, { x: a.x * width, y: height - a.y * height - h, width: w, height: h });
          }
        }
      }
      const out = await PDFDocument.create();
      for (const entry of order) {
        const [cp] = await out.copyPages(src, [entry.index]);
        if (entry.rotation) cp.setRotation(degrees(entry.rotation));
        out.addPage(cp);
      }
      const outBytes = await out.save();
      const b64 = abToBase64(outBytes.buffer);
      await api.post("/docs/edited", {
        title: saveTitle.trim() || "documento editado",
        pdf_base64: b64,
        folder_id: meta.folder_id || "",
        overwrite,
        original_id: id,
      });
      toast.success(overwrite ? "PDF actualizado" : "PDF editado guardado en Documentos");
      setShowSaveDlg(false);
      navigate("/documentos");
    } catch (err) {
      if (err.response?.status === 402) { toast.error(err.response.data.detail); setCoffeeOpen(true); }
      else toast.error(formatApiError(err.response?.data?.detail) || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  // ---- Café gate ----
  if (status && !active) {
    return (
      <div className={`${PAGE} font-body`}>
        <Navbar />
        <div className="max-w-lg mx-auto p-10 text-center">
          <div className="w-16 h-16 rounded-2xl bg-amber-500/10 text-amber-500 flex items-center justify-center mx-auto mb-5"><Coffee size={30} /></div>
          <h1 className="font-heading font-light text-3xl mb-3">Editar PDF necesita café activo ☕</h1>
          <p className="font-body text-slate-500 dark:text-slate-400 mb-6">Con una taza activa desbloqueas el editor completo: anotar, resaltar, escribir, firmar y organizar páginas.</p>
          <div className="flex justify-center gap-3">
            <button onClick={() => navigate("/documentos")} className={BTN_GHOST} data-testid="gate-back">Volver</button>
            <button onClick={() => setCoffeeOpen(true)} className={BTN_PRIMARY} data-testid="gate-recharge"><Coffee size={16} /> Recargar café</button>
          </div>
        </div>
        <CoffeeModal open={coffeeOpen} onClose={() => { setCoffeeOpen(false); refresh(); }} />
      </div>
    );
  }

  const strokes = [...pageAnns, ...(drawing.current ? [drawing.current] : [])];
  const cw = ptSize.w * scale;
  const ch = ptSize.h * scale;

  return (
    <div className={`h-screen flex flex-col ${PAGE} font-body`}>
      <Navbar />
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-200 dark:border-slate-800 flex-wrap">
        <button onClick={() => navigate("/documentos")} className={ICON_BTN} data-testid="editor-back"><ArrowLeft size={18} /></button>
        <span className="font-heading font-medium text-sm truncate max-w-[180px]">{meta.filename}</span>
        <div className="w-px h-6 bg-slate-200 dark:bg-slate-700 mx-1" />
        {TOOLS.map(({ id: tid, label, Icon }) => (
          <button key={tid} onClick={() => chooseTool(tid)} title={label}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${tool === tid ? "bg-indigo-600 text-white" : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"}`}
            data-testid={`tool-${tid}`}>
            <Icon size={15} /> <span className="hidden sm:inline">{label}</span>
          </button>
        ))}
        {(tool === "pen" || tool === "text") && (
          <div className="flex items-center gap-1 ml-1">
            {COLORS.map((c) => (
              <button key={c} onClick={() => setColor(c)} className={`w-5 h-5 rounded-full border-2 ${color === c ? "border-indigo-500 scale-110" : "border-transparent"}`} style={{ background: c }} data-testid={`color-${c}`} />
            ))}
          </div>
        )}
        {tool !== "pages" && pageAnns.length > 0 && (
          <button onClick={undo} className={`${BTN_GHOST} py-1.5 text-xs`} data-testid="undo-annotation"><Undo2 size={14} /> Deshacer</button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => setPageIdx((p) => Math.max(0, p - 1))} disabled={pageIdx === 0} className={ICON_BTN} data-testid="prev-page"><ChevronLeft size={16} /></button>
          <span className="font-mono2 text-xs w-16 text-center">{pageIdx + 1} / {numPages}</span>
          <button onClick={() => setPageIdx((p) => Math.min(numPages - 1, p + 1))} disabled={pageIdx >= numPages - 1} className={ICON_BTN} data-testid="next-page"><ChevronRight size={16} /></button>
          <button onClick={() => { setSaveTitle((meta.filename || "documento").replace(/\.pdf$/i, "") + " (editado)"); setShowSaveDlg(true); }} className={BTN_PRIMARY} data-testid="save-pdf"><Save size={15} /> Guardar</button>
        </div>
      </div>

      <div className="flex-1 overflow-auto bg-slate-100 dark:bg-slate-950 relative">
        {loading ? (
          <div className="h-full flex items-center justify-center text-slate-400"><Loader2 className="animate-spin" size={26} /></div>
        ) : tool === "pages" ? (
          <PagesPanel pdf={pdf} order={order} rotate={rotate} delPage={delPage} movePage={movePage} />
        ) : (
          <div className="flex justify-center py-8">
            <div className="relative shadow-xl" style={{ lineHeight: 0 }}>
              <canvas ref={canvasRef} data-testid="pdf-canvas" />
              {/* Annotation overlay */}
              <div
                className="absolute inset-0"
                style={{ cursor: tool === "select" ? "default" : "crosshair", touchAction: "none" }}
                onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
                data-testid="annotation-layer"
              >
                <svg className="absolute inset-0 pointer-events-none" width={cw} height={ch}>
                  {strokes.filter((a) => a.type === "pen" || a.type === "highlight").map((a, i) => (
                    <polyline key={i} points={a.points.map((p) => `${p.x * cw},${p.y * ch}`).join(" ")}
                      fill="none" stroke={a.color} strokeWidth={a.width * scale} strokeLinecap="round" strokeLinejoin="round"
                      opacity={a.type === "highlight" ? 0.35 : 1} />
                  ))}
                </svg>
                {pageAnns.filter((a) => a.type === "text").map((a, i) => (
                  <span key={`t${i}`} className="absolute font-body" style={{ left: `${a.x * 100}%`, top: `${a.y * 100}%`, color: a.color, fontSize: a.size * scale, whiteSpace: "nowrap" }}>{a.text}</span>
                ))}
                {pageAnns.filter((a) => a.type === "image").map((a, i) => (
                  <img key={`i${i}`} src={a.dataUrl} alt="firma" className="absolute" style={{ left: `${a.x * 100}%`, top: `${a.y * 100}%`, width: `${a.w * 100}%` }} />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Intro chooser */}
        {intro && !loading && (
          <div className="absolute inset-0 z-30 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="editor-intro">
            <div className="w-full max-w-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-2xl">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-heading font-medium text-xl">¿Qué quieres hacer?</h3>
                <button onClick={() => setIntro(false)} className={ICON_BTN} data-testid="intro-close"><X size={16} /></button>
              </div>
              <p className="font-body text-sm text-slate-500 dark:text-slate-400 mb-5">Elige una herramienta para empezar. Podrás cambiarla en cualquier momento.</p>
              <div className="grid grid-cols-2 gap-3">
                {TOOLS.filter((t) => t.id !== "select").map(({ id: tid, label, Icon }) => (
                  <button key={tid} onClick={() => chooseTool(tid)} className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 dark:border-slate-800 hover:border-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors text-left" data-testid={`intro-${tid}`}>
                    <div className="w-10 h-10 rounded-lg bg-indigo-500/10 text-indigo-500 flex items-center justify-center shrink-0"><Icon size={20} /></div>
                    <span className="font-heading font-medium text-sm">{label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {sigOpen && <SignaturePad onClose={() => setSigOpen(false)} onDone={(url) => { pendingSig.current = url; setSigOpen(false); toast.info("Toca el PDF donde quieres colocar tu firma"); }} />}

      {showSaveDlg && (
        <div className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !saving && setShowSaveDlg(false)} data-testid="save-dialog">
          <div className="w-full max-w-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading font-medium text-lg mb-4">Guardar PDF editado</h3>
            <label className="font-body text-xs text-slate-500 dark:text-slate-400">Nombre</label>
            <input value={saveTitle} onChange={(e) => setSaveTitle(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-lg px-4 py-2.5 text-sm mt-1 mb-4 focus:outline-none focus:ring-2 focus:ring-indigo-500/40" data-testid="save-title" />
            <label className="flex items-center gap-2 text-sm cursor-pointer mb-5">
              <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} data-testid="save-overwrite" />
              Sobrescribir el original (en vez de crear uno nuevo)
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowSaveDlg(false)} className={BTN_GHOST} disabled={saving} data-testid="save-cancel">Cancelar</button>
              <button onClick={doSave} className={BTN_PRIMARY} disabled={saving} data-testid="save-confirm">{saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={15} />} Guardar</button>
            </div>
          </div>
        </div>
      )}
      <CoffeeModal open={coffeeOpen} onClose={() => { setCoffeeOpen(false); refresh(); }} />
    </div>
  );
}

// ---------- Pages panel ----------
function PagesPanel({ pdf, order, rotate, delPage, movePage }) {
  const [thumbs, setThumbs] = useState({});
  useEffect(() => {
    let ignore = false;
    (async () => {
      if (!pdf) return;
      const out = {};
      for (const entry of order) {
        if (out[entry.index]) continue;
        const page = await pdf.getPage(entry.index + 1);
        const vp = page.getViewport({ scale: 0.3 });
        const c = document.createElement("canvas");
        c.width = vp.width; c.height = vp.height;
        await page.render({ canvasContext: c.getContext("2d"), viewport: vp }).promise;
        out[entry.index] = c.toDataURL();
      }
      if (!ignore) setThumbs(out);
    })();
    return () => { ignore = true; };
  }, [pdf, order]);

  return (
    <div className="p-6" data-testid="pages-panel">
      <p className="font-body text-sm text-slate-500 dark:text-slate-400 mb-4">Reordena, gira o elimina páginas. Los cambios se aplican al guardar.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {order.map((entry, i) => (
          <div key={`${entry.index}-${i}`} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-2 flex flex-col gap-2" data-testid={`page-thumb-${i}`}>
            <div className="bg-slate-100 dark:bg-slate-800 rounded flex items-center justify-center overflow-hidden" style={{ minHeight: 120 }}>
              {thumbs[entry.index] ? <img src={thumbs[entry.index]} alt="" style={{ transform: `rotate(${entry.rotation}deg)` }} className="max-h-40 transition-transform" /> : <Loader2 className="animate-spin text-slate-400" size={18} />}
            </div>
            <div className="flex items-center justify-between">
              <span className="font-mono2 text-[10px] text-slate-400">Pág {i + 1}</span>
              <div className="flex gap-0.5">
                <button onClick={() => movePage(i, -1)} className={ICON_BTN} title="Subir" data-testid={`page-up-${i}`}><ArrowUp size={12} /></button>
                <button onClick={() => movePage(i, 1)} className={ICON_BTN} title="Bajar" data-testid={`page-down-${i}`}><ArrowDown size={12} /></button>
                <button onClick={() => rotate(i, -1)} className={ICON_BTN} title="Girar izq" data-testid={`page-rotl-${i}`}><RotateCcw size={12} /></button>
                <button onClick={() => rotate(i, 1)} className={ICON_BTN} title="Girar der" data-testid={`page-rotr-${i}`}><RotateCw size={12} /></button>
                <button onClick={() => delPage(i)} className={ICON_BTN} title="Eliminar" data-testid={`page-del-${i}`}><Trash2 size={12} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
